
/**
 * URL API SERVER
 */
import { API_URL } from '@/App.settings';

/**
 * Axios
 */
import axios from 'axios';

/**
 * Composants VUE et VUEX
 */
import { Vue } from 'vue-property-decorator';
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';

/**
 * Traduction
 */
import { i18n } from '@/main';

/**
 * Classes & Enums
 */ 
import Dictionary from '@/types/Dictionary';
import { SMAContract, SMADistributorForm } from '@/types/Administration/SMA/SMADistributorForm';
import { Resource, SMACertificate } from '@/types/Administration/SMA/SMACertificate';
import { CustomerInfo, ParticularCustomerInformation, ProfessionalCustomerInformation } from '@/types/Administration/SMA/CustomerInfo';
import { Meter } from '@/types/Administration/SMA/MetersInformations';
import CertificateStateEnum from '@/enums/Administration/SMA/CertificateStateEnum';

const customerMap: Record<string, (el: any) => CustomerInfo> = {
    "ParticularCustomerInformation": (el) => new ParticularCustomerInformation(el),
    "ProfessionalCustomerInformation": (el) => new ProfessionalCustomerInformation(el)
};

const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
    });
};

/**
 * Agregateur de paramètres d'appels API
 * @param extraData Paramètres d'entrés API
 * @returns 
 */
const getApiArgs = (extraData: Record<string, any> = {}) => ({
    Token: (Vue.cookies.get('access_token') as string) || "",
    ...extraData
});

/**
 * Lecture d'un fichier en base64
 * @param file Fichier
 * @returns 
 */
const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
    });
};

/**
 * Initialisation du context
 * @param el Certificat
 * @returns 
 */
const hydrateCertificate = (el: any): SMACertificate => {
    const customerFactory = customerMap[el.CustomerInfo.Type];
    let customerInfo = null;

    if (customerFactory) {
        customerInfo = customerFactory(el.CustomerInfo);
    }

    return new SMACertificate({ ...el, customerInfo });
};

// Interface standard pour les réponses du Store
export interface StoreResult<T = any> {
    success: boolean;
    message?: string;
    data?: T;
}

/**
 * STORE POUR LA GESTION DES MANDATS DISTRIBUTEURS
 */
@Module({ namespaced: true })
class CertificateStore extends VuexModule {

    // #region State
    private CertificatesList: Array<SMACertificate> = [];
    private CurrentCertificate: SMACertificate | null = null;
    private FormList: Dictionary<SMADistributorForm> = {};
    private Contracts: Array<SMAContract> = [];
    private TariffCodes: Array<any> = [];
    private showArchived: boolean = false;

    // #endregion

    // #region Getters
    public get GetCertificates() { 
        return this.CertificatesList.filter(cert => this.showArchived ? cert.Status == CertificateStateEnum.Archived : cert.Status != CertificateStateEnum.Archived);
    }

    public get GetForms() { return this.FormList; }
    public get GetContracts() { return this.Contracts; }

    /**
     * Liste des codes tarifaires pour les données techniques des compteurs
     */
    public get GetTariffCodes() { return this.TariffCodes; }

    public get GetCurrentCertificate(): SMACertificate | null {
        return this.CurrentCertificate;
    }

    // #endregion

    // #region Mutations

    /**
     * Reset du store lors de la fermeture du composant de gestion des mandats
     */
    @Mutation
    private toggleShowArchived(value: boolean) {
        this.showArchived = value;
    }

    /**
     * Reset du store lors de la fermeture du composant de gestion des mandats
     */
    @Mutation
    private reset() {
        this.CertificatesList = [];
        this.FormList = {};
        this.CurrentCertificate = null; 
    }
    /**
     * Chargement des formulaires distributeur
     * @param items Dictionnaire des formulaires distributeurs
     */
    @Mutation
    private loadForms(items: Dictionary<SMADistributorForm>) {
        this.FormList = items;
    }

    /**
     * Chargement des contrats
     * @param items Liste de contrats
     */
    @Mutation
    private loadContracts(items: Array<SMAContract>) {
        this.Contracts = items;
    }

    /**
     * Chargement des codes tarifaires
     */
    @Mutation
    private loadTariffCodes(items: Array<any>) {
        this.TariffCodes = items;
    }

    /**
     * Hydratation des données brutes données par le serveur
     * @param rawData Données brutes
     */
    @Mutation
    private hydrateSMACertificate(rawData: Array<any>): void {
        const newCertificates = rawData.map(el => hydrateCertificate(el));
        newCertificates.forEach(cert => {
            const index = this.CertificatesList.findIndex(x => x.CertificateId == cert.CertificateId);
            if (index > -1) {
                this.CertificatesList.splice(index, 1, cert);
            } else {
                this.CertificatesList.push(cert);
            }
        });
    }

    /**
     * Supression d'un mandat de la liste
     * @param item Mandat à supprimer
     */
    @Mutation
    private RemoveCertificate(item: SMACertificate) {
        this.CertificatesList = this.CertificatesList.filter(cert => cert.CertificateId !== item.CertificateId);
    }

    /**
     * Set le certificat courant
     */
    @Mutation
    private setCurrentCertificate(certificate: SMACertificate) {
        this.CurrentCertificate = this.CertificatesList.find(cert => cert.CertificateId === certificate?.CertificateId) || certificate;
    }

    /**
     * Set le mandat préparé pour le renouvellement courant.
     * L'objet est conservé tel quel : son identifiant étant celui de l'ancien mandat,
     * la correspondance par identifiant retomberait sur le mandat d'origine.
     */
    @Mutation
    private setCurrentRenewalCertificate(certificate: SMACertificate) {
        this.CurrentCertificate = certificate;
    }

    // #endregion

    // #region Actions

    /**
     * Reset du store lors de la fermeture du composant de gestion des mandats
     */
    @Action({ rawError: true })
    public ToggleShowArchived(value: boolean) {
        this.context.commit('toggleShowArchived', value);
    }

    /**
     * Action de reset du store
     */
    @Action({ rawError: true })
    public Clear() 
    {
        this.context.commit('reset');
    }

    /**
     * Action pour définir le certificat sélectionné (accessible depuis n'importe quel composant)
     * @param certificate Le certificat à sélectionner (ou null pour le désélectionner)
     */
    @Action({ rawError: true })
    public SetCurrentCertificate(certificate: SMACertificate | null) {
        this.context.commit('setCurrentCertificate', certificate);
    }

    /**
     * Action pour définir le mandat préparé pour un renouvellement
     * (nouvelle date de fin, annexes vidées) : l'objet est conservé tel quel.
     * @param certificate Mandat préparé pour le renouvellement
     */
    @Action({ rawError: true })
    public SetRenewalCertificate(certificate: SMACertificate) {
        this.context.commit('setCurrentRenewalCertificate', certificate);
    }
    
    /**
     * Suppression d'un contrat client (Direct) lié à la base du client
     * @param payload Identifiant du contrat à supprimer
     * @returns StoreResult (Success, message)
     */
    @Action({ rawError: true })
    public async DeleteClientContract(payload: { ContractId: number }): Promise<StoreResult>
    {
        this.context.dispatch("ShowLoadingMask", i18n.t("Save"), { root: true });

        try {
            await axios.post(
                `${API_URL}SMAController/DeleteClientContract`,
                getApiArgs(payload)
            );

            return { success: true, message: i18n.t("ContractDeleted").toString() };
        } catch (e: any) {
            return { success: false, message: typeof e.response?.data === 'string' ? e.response.data : (e.response?.data?.Message || e.message || i18n.t("UnknownError").toString()) };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Sauvegarde des données techniques de contrat d'un compteur (segments, code tarifaire)
     * @param payload Mandat, compteur et points de données techniques au format ENEDIS
     * @returns StoreResult (Success, message)
     */
    @Action({ rawError: true })
    public async SaveTechnicalData(payload: { CertificateId: number, RaePceNumber: string, Points: Array<any> }): Promise<StoreResult>
    {
        this.context.dispatch("ShowLoadingMask", i18n.t("Save"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/SaveTechnicalData`,
                getApiArgs(payload)
            );

            return { success: true, message: i18n.t("TechnicalDataSaved").toString(), data: (response.data as any)?.technicalData };
        } catch (e: any) {
            return { success: false, message: typeof e.response?.data === 'string' ? e.response.data : (e.response?.data?.Message || e.message || i18n.t("UnknownError").toString()) };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Téléchargement du modèle d'import des données techniques d'un mandat
     * Le modèle contient une ligne par compteur du mandat, pré-remplie avec les données courantes
     * @param payload CertificateId : mandat, Distributor : distributeur du mandat
     * @returns StoreResult (Success, message)
     */
    @Action({ rawError: true })
    public async DownloadTechDataTemplate(payload: { CertificateId: any, Distributor: any }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DownloadInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/DownloadTechDataTemplate`,
                getApiArgs({ CertificateId: payload.CertificateId, Distributor: payload.Distributor }),
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));

            let fileName = "Modele_Donnees_Techniques.xlsx";
            const disposition = response.headers['content-disposition'];

            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        }
        catch (e: any) {
            let errorMessage = i18n.t("ErrorDuringDownload").toString();

            if (e.response && e.response.data instanceof Blob) {
                try {
                    const errorJson = await e.response.data.text();
                    const parsedError = JSON.parse(errorJson);
                    errorMessage = parsedError.Message || parsedError.ExceptionMessage || parsedError.message || errorMessage;
                } catch (parseError) {
                    console.error("Erreur parsing:", parseError);
                }
            } else if (e.message) {
                errorMessage = e.message;
            }

            return { success: false, message: errorMessage };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Import des données techniques des compteurs d'un mandat par fichier Excel
     * La date de dernière modification de la formule tarifaire est la clé : une ligne portant
     * une date déjà présente met à jour le point existant, une nouvelle date ajoute un point
     * @param payload CertificateId : mandat, Distributor : distributeur du mandat, File : fichier Excel
     * @returns StoreResult (Success, message, data : technicalData par compteur)
     */
    @Action({ rawError: true })
    public async ImportTechData({ CertificateId, Distributor, File }: { CertificateId: any, Distributor: any, File: File }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("ImportInProgress"), { root: true });

        try {
            const formData = new FormData();
            formData.append("File", File);
            formData.append("CertificateId", String(CertificateId));
            formData.append("Distributor", String(Distributor));

            const args = getApiArgs();
            Object.keys(args).forEach(key => {
                if (args[key] !== undefined && args[key] !== null) {
                    formData.append(key, args[key]);
                }
            });

            const response = await axios.post(`${API_URL}SMAController/UploadTechData`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            return { success: true, message: i18n.t("TechDataImported").toString(), data: response.data };
        } catch (e: any) {
            const data = e.response?.data;

            return { success: false, message: (typeof data === 'string' ? data : (data?.Message || e.message)) || i18n.t("UnknownError").toString(), data };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Création d'un contrat client (Direct) pour le distributeur sélectionné
     * @param payload Nom, type distributeur et valeurs des champs du contrat déclarés côté serveur
     * @returns StoreResult (Success, message)
     */
    @Action({ rawError: true })
    public async SaveClientContract(payload: { ContractId?: number, Name: string, Type: number, Fields: any }): Promise<StoreResult>
    {
        this.context.dispatch("ShowLoadingMask", i18n.t("Save"), { root: true });

        try {
            await axios.post(
                `${API_URL}SMAController/SaveClientContract`,
                getApiArgs(payload)
            );

            return { success: true, message: i18n.t("ClientContractSaved").toString() };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Chargement des formulaires distributeurs
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async LoadSMAForms(): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("LoadingSMAForms"), { root: true });
        
        try {
            const response = await axios.post(
                `${API_URL}SMAController/GetSMAForms`, 
                getApiArgs()
            );
            const forms = (response.data as any)["result"] || response.data; 
            this.context.commit('loadForms', forms);
            
            const contracts = (response.data as any)["contracts"] || response.data; 
            this.context.commit('loadContracts', contracts);

            const tariffCodes = (response.data as any)["tariffCodes"] || []; 
            this.context.commit('loadTariffCodes', tariffCodes);

            return { success: true };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Chargement des mandats
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async Load(): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("LoadingCertificates"), { root: true });

        try {
            const response = await axios.post<any>(
                `${API_URL}SMAController/GetCertificatesList`, 
                getApiArgs()
            );
            const data = (response.data as any)["result"]; 
            this.context.commit('hydrateSMACertificate', data || []);

            // Re-lien du mandat selectionne sur l'objet rafraichi de la liste
            // (sinon il pointe sur l'ancien objet et les donnees techniques saisies n'apparaissent pas sans rechargement)
            if (this.CurrentCertificate)
                this.context.commit('setCurrentCertificate', this.CurrentCertificate);

            return { success: true };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Sauvegarde d'un mandat
     * @param param0 item : Mandat, action: Action à réaliser sur le mandat (Sauvegarde, Modifiation ...)
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async Save({ item, action }: { item: any, action: string }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("SaveInProgress"), { root: true });

        try {
            const pendingFiles: File[] = (item.Resources || []).filter((r: any) => r.File instanceof File).map((r: any) => r.File as File);

            const payload = { ...item, resources: (item.Resources || []).filter((r: any) => !(r.File instanceof File)) };

            const response = await axios.post(
                `${API_URL}SMAController/${action}`, 
                getApiArgs({ Certificate: payload })
            );

            this.context.commit('toggleShowArchived');

            const rawResult = response.data["result"];
            this.context.commit('hydrateSMACertificate', Array.isArray(rawResult) ? rawResult : (rawResult ? [rawResult] : []));

            // Renouvellement : mise à jour du mandat archivé dans la liste
            const archivedResult = response.data["archived"];

            if (archivedResult) {
                this.context.commit('hydrateSMACertificate', [archivedResult]);
            }

            // Upload des fichiers par lots une fois le mandat sauvegardé :
            // une fenêtre non fermable affiche la progression globale et le statut de chaque fichier
            if (pendingFiles.length > 0 && rawResult) {
                const certificate = Array.isArray(rawResult) ? rawResult[0] : rawResult;

                // Upload g\u00e9n\u00e9rique pilot\u00e9 par le store UploadStore : fen\u00eatre de progression, lots, reprises individuelles
                const result = await this.context.dispatch('UploadStore/UploadFiles', {
                    Endpoint: `${API_URL}SMAController/AddResources`,
                    Args: { Certificate: JSON.stringify(certificate) },
                    Files: pendingFiles,
                    OnBatchSuccess: (batchResult: any) => {
                        this.context.commit('hydrateSMACertificate', Array.isArray(batchResult) ? batchResult : (batchResult ? [batchResult] : []));
                    }
                }, { root: true }) as { success: boolean, message?: string };

                if (!result || !result.success) {
                    return { success: false, message: (result && result.message) || i18n.t("UnknownError").toString() };
                }
            }

            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Validation client du mandat
     * @param param0 Certificate: mandat, Base64File: fichier PDF, Password: mot de passe
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async ClientValidation({ Certificate: certificate, Base64File: base64File, Password: password }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("ValidationInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/ClientValidation`,
                getApiArgs({ Certificate: certificate, Base64File: base64File, Password: password })
            );

            const rawResult = response.data["result"];
            this.context.commit('hydrateSMACertificate', Array.isArray(rawResult) ? rawResult : (rawResult ? [rawResult] : []));

            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString()  };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Validation client du mandat sans session
     * @param param0 Token: token, Base64File: fichier PDF
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async ClientValidationSessionLess({ Token: token, Base64File: base64File }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("ValidationInProgress"), { root: true });

        console.debug("Client validation session less", token, base64File);

        try {
            await axios.post(
                `${API_URL}SMAController/ClientValidationSessionLess`,
                getApiArgs({ Token: token, Base64File: base64File })
            );

            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }
    
    /**
     * Validation superviseur du mandat
     * @param param0 Certificate: mandat, Password: mot de passe
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async SupervisorValidation({ Certificate: certificate, Password: password }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("ValidationInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/SupervisorValidation`,
                getApiArgs({ Certificate: certificate, Password: password })
            );

            const rawResult = response.data["result"];
            this.context.commit('hydrateSMACertificate', Array.isArray(rawResult) ? rawResult : (rawResult ? [rawResult] : []));

            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Modification du pdf d'un mandat en god mode
     * @param param0 
     */
    @Action({ rawError: true })
    public async UpdateCertificate({ Certificate: certificate, Base64File: base64File }: { Certificate: SMACertificate, Base64File: string }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("ValidationInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/UpdateCertificate`,
                getApiArgs({ Certificate: certificate, Base64File: base64File })
            );

            const rawResult = response.data["result"];
            this.context.commit('hydrateSMACertificate', Array.isArray(rawResult) ? rawResult : (rawResult ? [rawResult] : []));

            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString()  };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Rafraichissement des données d'un compteur
     * @param param0 Certifiate: mandat, Id : Identifiant compteur
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async RefreshMeter({ Certificate, Id }: { Certificate: SMACertificate, Id: string }): Promise<StoreResult<{ Meters: Array<Meter> }>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("RefreshInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/RefreshMeter`,
                getApiArgs({ Certificate: JSON.stringify(Certificate), Id: Id })
            );
            
            const rawResult = response.data["result"];
            this.context.commit('hydrateSMACertificate', Array.isArray(rawResult) ? rawResult : (rawResult ? [rawResult] : []));
            this.context.commit('setCurrentCertificate', Certificate);

            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        } 
        catch(e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Renouvellement de l'abonnement sur le compteur
     * @param param0 Mandat
     * @returns Identifiant compteur
     */
    @Action({ rawError: true })
    private async RenewSubscribtion({ Certificate, id }: { Certificate: SMACertificate, id: string }) {
        this.context.dispatch("ShowLoadingMask", i18n.t("RenewInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/RenewSubscribtion`,
                getApiArgs({ Certificate: JSON.stringify(Certificate), Id: id })
            );
            return { 
                success: true,
                message: i18n.t('RenewSubscriptionSuccess').toString(), 
                data: { Meters: (response.data.Meters || []).map(el => new Meter(el)) }
            };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Envoi d'un email au client pour validation
     * @param param0 item : Mandat
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async SendEmail({ item }: { item: SMACertificate }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("SendingInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/SendEmail`, 
                getApiArgs({ Certificate: JSON.stringify(item) })
            )
            return { success: true, message: i18n.t("EmailSent").toString() };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Suppression d'un mandat
     * @param param0 item : Mandat
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async Delete({ item }: { item: SMACertificate }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DeleteInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/Delete`, 
                getApiArgs({ Certificate: JSON.stringify(item) })
            )
            
            this.context.commit('RemoveCertificate', item);
            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Import de compteur en masse
     * @param param0  file: fichier (xlsx, csv, txt..), types: Type objet (Compteur électrique, Compteur gaz)
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async ImportRaeList({ item, file, types }: { item: SMACertificate, file: File, types: any }): Promise<StoreResult<{ Meters: Array<Meter>, Errors: Array<string> }>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("ImportInProgress"), { root: true });

        try{
            const fileContent = file ? await readFileAsBase64(file) : "";
            const args = getApiArgs({
                Certificate: JSON.stringify(item),
                Types: types,
                Extension: file ? file.name.split('.').pop() : "",
                FileName: file ? file.name : "",
                FileBase64: fileContent
            });

            const response = await axios.post<{ Meters: Array<any>, Errors: Array<string> }>(
                `${API_URL}SMAController/RaeImport`, 
                args
            )
            
            return {
                success: true,
                message: i18n.t('ImportSuccess').toString(),
                data: {
                    Meters: (response.data.Meters || []).map(el => new Meter(el)),
                    Errors: response.data.Errors || []
                }
            };
        }
        catch(e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Récupération de la liste des compteurs suivant un noeud LBO
     * @param param0 
     * @returns 
     */
    @Action({ rawError: true })
    public async GetMeterByLO({ item, LoId, Types }: { item: SMACertificate, LoId: string, Types: string[] }): Promise<StoreResult<Array<Meter>>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("LoadingMeters"), { root: true });
        try {
            const response = await axios.post(
                `${API_URL}SMAController/GetMeterByLO`,
                getApiArgs({ Certificate: JSON.stringify(item), LOId: LoId, Types: Types })
            )

            console.debug("GetMeterByLO response", response);

            return {
                success: true,
                message: i18n.t("ImportSuccess").toString(),
                data: (response.data as any)["result"].map(el => new Meter(el))
            };
        }
        catch(e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Vérification des informations du compteur
     * @param param0 mandat, compteur
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async CheckThisMeter({ item, meter }: { item: SMACertificate, meter: Meter }): Promise<StoreResult<Array<Meter>>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("CheckingMeter"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/CheckThisMeter`,
                getApiArgs({ Certificate: JSON.stringify(item), Meter: JSON.stringify(meter) })
            );

            return { 
                success: true, 
                message: i18n.t("ImportSuccess").toString(),
                data: (response.data as any)["result"].map(el => new Meter(el))
            };
        }
        catch(e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Importation de compteurs restant sans mandat
     * @param param0 types: Type objet (Compteur électrique, Compteur gaz)
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async ImportRemainMeters({ item, types }: { item: SMACertificate, types: string[] }): Promise<StoreResult<Array<Meter>>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("ImportInProgress"), { root: true });

        try{
            const response = await axios.post(
                `${API_URL}SMAController/ImportRemainMeters`,
                getApiArgs({ Certificate: JSON.stringify(item), Types: types })
            )

            return { 
                success: true, 
                message: i18n.t("ImportSuccess").toString(),
                data: (response.data as any)["result"].map(el => new Meter(el))
            };
        } 
        catch(e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Téléchargement du fichier PDF
     * @param param0 item : Mandat, resource : Ressource
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async Generate({ item, resources }: { item: SMACertificate, resources: Resource[] }): Promise<StoreResult<Blob>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DownloadInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/Generate`,
                getApiArgs({ Certificate: JSON.stringify(item), Resources: JSON.stringify(resources) }), 
                { responseType: 'blob' }
            );

            return { success: true, data: response.data }; 
        } 
        catch (e: any) {
            let errorMessage = i18n.t("ErrorDuringDownload").toString();

            if (e.response && e.response.data instanceof Blob) {
                const errorJson = await e.response.data.text();
                
                try {
                    const parsedError = JSON.parse(errorJson);
                    errorMessage = parsedError.Message || parsedError.message || errorMessage;
                } catch (parseError) {
                    console.error("Erreur lors du parsing du message d'erreur:", parseError);
                }
            }

            return { success: false, message: errorMessage };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Téléchargement du fichier PDF
     * @param param0 item : Mandat, resource : Ressource
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async Download({ item, resources }: { item: SMACertificate, resources: Resource[] }): Promise<StoreResult<Blob>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DownloadInProgress"), { root: true });
        
        try {
            const response = await axios.post(
                `${API_URL}SMAController/Download`,
                getApiArgs({ Certificate: JSON.stringify(item), Resources: JSON.stringify(resources) }), 
                { responseType: 'blob' } // INDISPENSABLE pour recevoir le PDF fusionné
            );

            return { success: true, data: response.data }; 
        } 
        catch (e: any) {
            let errorMessage = i18n.t("ErrorDuringDownload").toString();

            if (e.response && e.response.data instanceof Blob) {
                const errorJson = await e.response.data.text();
                try {
                    const parsedError = JSON.parse(errorJson);
                    errorMessage = parsedError.Message || parsedError.message || errorMessage;
                } catch {
                    errorMessage = errorJson || errorMessage;
                }
            }

            return { success: false, message: errorMessage };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }
    
    /**
     * Téléchargement du zip du mandat avec toutes les ressources
     * @param param0 item : Mandat, resource : Ressource
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    private async DownloadAll( item : SMACertificate ): Promise<StoreResult<Blob>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DownloadInProgress"), { root: true });

        console.debug("DownloadAll", item);

        try {
            const response = await axios.post(
                `${API_URL}SMAController/DownloadAll`,
                getApiArgs({ Certificate: JSON.stringify(item) }), 
                { responseType: 'blob' }
            );

            // 1. On s'assure de forcer le type du Blob à "application/zip"
            const blob = new Blob([response.data], { type: 'application/zip' });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // 2. On ajoute explicitement ".zip" à la fin du nom du fichier
            link.setAttribute('download', `${item.CertificateName}.zip`); 
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true, data: blob }; 
        }
        catch (e: any) {
            let errorMessage = i18n.t("ErrorDuringDownload").toString();

            if (e.response && e.response.data instanceof Blob) {
                const errorJson = await e.response.data.text();
                try {
                    const parsedError = JSON.parse(errorJson);
                    errorMessage = parsedError.Message || parsedError.message || errorMessage;
                } catch {
                    errorMessage = errorJson || errorMessage;
                }
            }

            return { success: false, message: errorMessage };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Suppression d'une ressource du mandat
     * @param param0 item : Mandat, resource : Ressource
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async DeleteResource({ item, resource }: { item: SMACertificate, resource: Resource }): Promise<StoreResult> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DownloadInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/DeleteResource`,
                getApiArgs({ Certificate: JSON.stringify(item), Resource: JSON.stringify(resource) })
            );
            
            const rawResult = response.data["result"];
            this.context.commit('hydrateSMACertificate', Array.isArray(rawResult) ? rawResult : (rawResult ? [rawResult] : []));

            return { success: true, message: i18n.t("SuccessfulOperation").toString() };
        } 
        catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString() };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Téléchargement du fichier PDF par token
     * @param param0 token : Token de téléchargement
     * @returns StoreResult (Success, message, data)
     */
    @Action({ rawError: true })
    public async DownloadByToken({ token }: { token: string }): Promise<StoreResult<Blob>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DownloadInProgress"), { root: true });

        try {
            const response = await axios.post(
                `${API_URL}SMAController/DownloadByToken`,
                getApiArgs({ Token: token }), 
                { responseType: 'blob' }
            );
            
            return { success: true, data: response.data }; 
        } 
        catch (e: any) {
            let errorMessage = i18n.t("ErrorDuringDownload").toString();

            if (e.response && e.response.data instanceof Blob) {
                const errorJson = await e.response.data.text();
                
                try {
                    const parsedError = JSON.parse(errorJson);
                    errorMessage = parsedError.Message || parsedError.message || errorMessage;
                } catch (parseError) {
                    console.error("Erreur lors du parsing du message d'erreur:", parseError);
                }
            }

            return { success: false, message: errorMessage };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }
    
    /**
     * Télécharge le rapport des récupérations de données GRD
     * @param param0 items : Mandats distributeurs
     * @returns StoreResult Blob
     */
    @Action({ rawError: true })
    public async DownloadReport({ items }: { items: SMACertificate[] }): Promise<StoreResult<Blob>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DownloadInProgress"), { root: true });

        try {
            const lightItems: SMACertificate[] = items.map(c => new SMACertificate({
                CertificateId: c.CertificateId,
                Distributor: c.Distributor
            }));

            const response = await axios.post(
                `${API_URL}SMAController/DownloadReport`,
                getApiArgs({ Certificates: JSON.stringify(lightItems) }),
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            
            let fileName = "Rapport_SMA.xlsx";
            const disposition = response.headers['content-disposition'];
            
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) { 
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true, data: response.data }; 
        } 
        catch (e: any) {
            let errorMessage = i18n.t("ErrorDuringDownload").toString();

            if (e.response && e.response.data instanceof Blob) {
                try {
                    const errorJson = await e.response.data.text();
                    const parsedError = JSON.parse(errorJson);
                    
                    errorMessage = parsedError.Message || parsedError.ExceptionMessage || parsedError.message || errorMessage;
                } catch (parseError) {
                    console.error("Erreur lors du parsing du message d'erreur:", parseError);
                }
            } else if (e.message) {
                errorMessage = e.message;
            }

            return { success: false, message: errorMessage };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Téléchargement du modèle Excel pour l'import en masse
     * @returns StoreResult
     */
    @Action({ rawError: true })
    public async DownloadMassImportTemplate(): Promise<StoreResult<null>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("DownloadInProgress"), { root: true });

        try {
            // Utilisation de POST pour intégrer getApiArgs() contenant le Token
            const response = await axios.post(
                `${API_URL}SMAController/DownloadTemplate`,
                getApiArgs(),
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));

            let fileName = "Modele_Import_Mandats.xlsx";
            const disposition = response.headers['content-disposition'];

            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) { 
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // On retourne null dans la data car le téléchargement est géré ici
            return { success: true }; 
        } 
        catch (e: any) {
            let errorMessage = i18n.t("ErrorDuringDownload").toString();

            if (e.response && e.response.data instanceof Blob) {
                try {
                    const errorJson = await e.response.data.text();
                    const parsedError = JSON.parse(errorJson);
                    errorMessage = parsedError.Message || parsedError.ExceptionMessage || parsedError.message || errorMessage;
                } catch (parseError) {
                    console.error("Erreur parsing:", parseError);
                }
            } else if (e.message) {
                errorMessage = e.message;
            }

            return { success: false, message: errorMessage };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Envoi du fichier Excel pour import en masse
     * @param param0 file : Fichier Excel sélectionné
     * @returns StoreResult
     */
    @Action({ rawError: true })
    public async UploadMassImport({ file }: { file: File }): Promise<StoreResult<Array<string>>> {
        this.context.dispatch("ShowLoadingMask", i18n.t("ImportInProgress"), { root: true });

        try {
            const formData = new FormData();
            formData.append("file", file);

            const args = getApiArgs();
            Object.keys(args).forEach(key => {
                if (args[key] !== undefined && args[key] !== null) {
                    formData.append(key, args[key]);
                }
            });
 
            const response = await axios.post(`${API_URL}SMAController/UploadMassImport`, formData, { 
                headers: { 'Content-Type': 'multipart/form-data' } 
            });

            const rawResult = response.data["result"];
            this.context.commit('hydrateSMACertificate', Array.isArray(rawResult) ? rawResult : (rawResult ? [rawResult] : []));

            return { success: true, message: i18n.t("ImportSuccess").toString(), data: response.data };
        } catch (e: any) {
            return { 
                success: false, 
                message: e.response?.data?.Message || e.message || i18n.t("UnknownError").toString(),
                data: e.response?.data
            };
        } finally {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }
    // #endregion
}

export default CertificateStore;